import { components } from "@/api-schema";
import { CompanyType } from "@/api-schema-enums";
import { Can, useAbility } from "@/casl-provider";
import { useVehicleValuationStartConfirmationMutation } from "@/common/api/vehicle-valuation/use-vehicle-valuation-start-confirmation.mutation";
import { buildVehicleValuationSearchBody } from "@/common/helpers/build-vehicle-valuation-search-body";
import { canVehicleValuationBeStarted } from "@/common/helpers/can-vehicle-valuation-be-started";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { VehicleValuationTable } from "@/components/data-table/predefined/vehicle-valuation-table/vehicle-valuation-table";
import { DataTableRow } from "@/components/data-table/types";
import { BranchFilter } from "@/components/filter/predefined/branch-filter";
import { BrandFilter } from "@/components/filter/predefined/brand-filter";
import { CompanyFilter } from "@/components/filter/predefined/company-filter";
import { ModelFamilyFilter } from "@/components/filter/predefined/model-family-filter";
import { ModelGenerationFilter } from "@/components/filter/predefined/model-generation-filter";
import { VehicleFilter } from "@/components/filter/predefined/vehicle-filter";
import { VehicleValuationConfirmerFilter } from "@/components/filter/predefined/vehicle-valuation-confirmer-filter";
import { VehicleValuationCreatedAtRangeFilter } from "@/components/filter/predefined/vehicle-valuation-created-at-range-filter";
import {
  VehicleValuationMarginGrossRangeFilter,
  VehicleValuationSalesGrossRangeFilter,
  VehicleValuationTradeGrossRangeFilter,
} from "@/components/filter/predefined/vehicle-valuation-price-range-filters";
import { VehicleValuationProviderFilter } from "@/components/filter/predefined/vehicle-valuation-provider-filter";
import { VehicleValuationRequesterFilter } from "@/components/filter/predefined/vehicle-valuation-requester-filter";
import { VehicleValuationStatusFilter } from "@/components/filter/predefined/vehicle-valuation-status-filter";
import { VehicleValuationTypeFilter } from "@/components/filter/predefined/vehicle-valuation-type-filter";
import { VehicleValuationValidityFilter } from "@/components/filter/predefined/vehicle-valuation-validity-filter";
import { VehicleValuationVatFilter } from "@/components/filter/predefined/vehicle-valuation-vat-filter";
import { VehicleFuelTypeFilter } from "@/components/filter/predefined/vehicle-fuel-type-filter";
import { VehicleStatusFilter } from "@/components/filter/predefined/vehicle-status-filter";
import {
  VehicleMileageRangeFilter,
  VehiclePowerKwRangeFilter,
  VehiclePowerPsRangeFilter,
} from "@/components/filter/predefined/vehicle-range-filters";
import { VehicleRegistrationDateRangeFilter } from "@/components/filter/predefined/vehicle-registration-date-range-filter";
import { VehicleTypeFilter } from "@/components/filter/predefined/vehicle-type-filter";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { getVehicleValuationRoute } from "@/modules/fab/vehicle-valuation/_helpers/get-vehicle-valuation-route";
import { userConfigStore } from "@/store/user-config-store";
import { PlayIcon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
type VehicleValuation = components["schemas"]["VehicleValuation"];

const VEHICLE_VALUATION_REFETCH_INTERVAL_MS = 20_000;

export function VehicleValuationView() {
  const { t } = useTranslation(["common", "fab", "masterdata"]);
  const [_, navigate] = useLocation();
  const ability = useAbility();
  const canManageVehicleValuation = ability.can("manage", "VehicleValuation");

  const startConfirmationMutation = useVehicleValuationStartConfirmationMutation();
  const dateFormat = userConfigStore((state) => state.dateFormat);

  const [filterValues, setFilterValues] = useState<Record<string, string[]>>({
    vehicles: [],
    brands: [],
    modelFamilies: [],
    modelGenerations: [],
    statuses: [],
    providers: [],
    vehicleStatus: [],
    requester: [],
    requesterCompany: [],
    requesterBranch: [],
    confirmedBy: [],
    valuationType: [],
    vatDeductible: [],
    validities: [],
    createdAt: [],
    tradeGross: [],
    salesGross: [],
    marginGross: [],
    fuelType: [],
    vehicleType: [],
    registrationDate: [],
    mileage: [],
    powerKw: [],
    powerPs: [],
  });

  const filters = useMemo(() => {
    const vehicleGroup = t("common:Vehicle");
    const valuationGroup = t("fab:FilterGroupValuation");
    const requesterGroup = t("fab:FilterGroupRequester");
    const confirmerGroup = t("fab:FilterGroupConfirmer");
    const dateAndValueGroup = t("fab:FilterGroupDateAndValue");

    return [
      new VehicleFilter().setGroup(vehicleGroup),
      new BrandFilter().setGroup(vehicleGroup),
      new ModelFamilyFilter({ body: { brand: filterValues.brands || [] } }).setGroup(vehicleGroup),
      new ModelGenerationFilter({
        body: {
          brand: filterValues.brands || [],
          modelFamily: filterValues.modelFamilies || [],
        },
      }).setGroup(vehicleGroup),
      new VehicleFuelTypeFilter().setGroup(vehicleGroup),
      new VehicleStatusFilter({ showAdminStatuses: ability.can("manage", "VehicleStatus") }).setGroup(vehicleGroup),
      new VehicleTypeFilter().setGroup(vehicleGroup),
      new VehicleRegistrationDateRangeFilter().setGroup(vehicleGroup),
      new VehicleMileageRangeFilter().setGroup(vehicleGroup),
      new VehiclePowerKwRangeFilter().setGroup(vehicleGroup),
      new VehiclePowerPsRangeFilter().setGroup(vehicleGroup),
      new VehicleValuationStatusFilter().setGroup(valuationGroup),
      new VehicleValuationProviderFilter().setGroup(valuationGroup),
      new VehicleValuationTypeFilter().setGroup(valuationGroup),
      new VehicleValuationVatFilter().setGroup(valuationGroup),
      new VehicleValuationValidityFilter().setGroup(valuationGroup),
      new VehicleValuationRequesterFilter({ id: "requester" })
        .setLabelOverrides({
          labelSingular: "fab:Requester",
          placeholder: "fab:SelectRequester",
          searchPlaceholder: "fab:SearchRequester",
        })
        .setGroup(requesterGroup),
      new CompanyFilter({ id: "requesterCompany", body: { type: [CompanyType.Alphartis] } })
        .setLabelOverrides({
          labelSingular: "fab:RequesterCompany",
          placeholder: "fab:SelectRequesterCompany",
          searchPlaceholder: "fab:SearchRequesterCompany",
        })
        .setGroup(requesterGroup),
      new BranchFilter({ id: "requesterBranch" })
        .setLabelOverrides({
          labelSingular: "fab:RequesterBranch",
          placeholder: "fab:SelectRequesterBranch",
          searchPlaceholder: "fab:SearchRequesterBranch",
        })
        .setGroup(requesterGroup),
      new VehicleValuationConfirmerFilter({ id: "confirmedBy" })
        .setLabelOverrides({
          placeholder: "fab:SelectConfirmer",
          searchPlaceholder: "fab:SearchConfirmer",
        })
        .setGroup(confirmerGroup),
      new VehicleValuationCreatedAtRangeFilter().setGroup(dateAndValueGroup),
      ...(canManageVehicleValuation
        ? [
            new VehicleValuationTradeGrossRangeFilter().setGroup(dateAndValueGroup),
            new VehicleValuationSalesGrossRangeFilter().setGroup(dateAndValueGroup),
            new VehicleValuationMarginGrossRangeFilter().setGroup(dateAndValueGroup),
          ]
        : []),
    ];
  }, [ability, filterValues.brands, filterValues.modelFamilies, canManageVehicleValuation, t]);

  const handleFilterChange = useCallback((filterId: string, values: string[]) => {
    setFilterValues((prev) => {
      const nextValues = {
        ...prev,
        [filterId]: values,
      };

      if (filterId === "brands") {
        nextValues.modelFamilies = [];
        nextValues.modelGenerations = [];
      }

      if (filterId === "modelFamilies") {
        nextValues.modelGenerations = [];
      }

      return nextValues;
    });
  }, []);

  const body = useMemo(
    () =>
      buildVehicleValuationSearchBody({
        filterValues,
        canManageVehicleValuation,
        dateFormat,
      }),
    [filterValues, canManageVehicleValuation, dateFormat],
  );

  const handleView = useCallback(
    (valuation: VehicleValuation) => {
      navigate(getVehicleValuationRoute(valuation));
    },
    [navigate],
  );

  const handleStartConfirmation = useCallback(
    async (valuation: VehicleValuation) => {
      await toastMutationStatus({
        mutationFn: () => startConfirmationMutation.mutateAsync({ uuid: valuation.uuid }),
        successMessage: t("masterdata:VehicleValuationConfirmationStarted"),
        errorMessage: t("masterdata:ErrorStartingVehicleValuationConfirmation"),
        onSuccess: () => navigate(`~/fab/list/${valuation.uuid}/confirm`),
      });
    },
    [navigate, startConfirmationMutation, t],
  );

  const customRowActions = useCallback(
    (row: DataTableRow<VehicleValuation>) => {
      if (!ability?.can("manage", "VehicleValuation")) {
        return [];
      }

      const actions = [];

      if (canVehicleValuationBeStarted(row.original.status)) {
        actions.push({
          label: t("masterdata:StartVehicleValuationConfirmation"),
          icon: <PlayIcon className="h-4 w-4" />,
          onClick: () => handleStartConfirmation(row.original),
          disabled: startConfirmationMutation.isPending,
        });
      }

      return actions;
    },
    [handleStartConfirmation, startConfirmationMutation.isPending, t, ability],
  );

  return (
    <Can I="read" a="VehicleValuation">
      <CenteredLayout
        variant={CenteredLayoutVariant.Table}
        title={t("fab:VehicleValuations")}
        subTitle={t("fab:VehicleValuationsDescription")}
      >
        <VehicleValuationTable
          body={body}
          filters={filters}
          filterValues={filterValues}
          filterContainerClassName="w-96"
          onFilterChange={handleFilterChange}
          onRowNavigate={getVehicleValuationRoute}
          onView={handleView}
          customRowActions={customRowActions}
          queryOptions={{ refetchInterval: VEHICLE_VALUATION_REFETCH_INTERVAL_MS }}
          canManageVehicleValuation={canManageVehicleValuation}
          showCreateButton={ability?.can("create", "VehicleValuation") ?? false}
          createButtonLink="~/fab/list/initialize/select-vehicle"
          createButtonText={t("fab:StartVehicleValuation")}
        />
      </CenteredLayout>
    </Can>
  );
}
